import { Middleware } from '@reduxjs/toolkit';
import { updateAnalysisProgress, setAnalysisStatus } from '../slices/mineralDiscoverySlice';
import { buildWebSocketUrl } from '../../config/apiClient';

interface WebSocketMessage {
  type: 'progress' | 'status' | 'completed' | 'error';
  data: {
    progress?: number;
    message?: string;
    estimatedTime?: number;
    analysisId?: string;
    results?: any;
    error?: string;
  };
}

class WebSocketManager {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private dispatch: any = null;

  connect(dispatch: any) {
    this.dispatch = dispatch;
    
    if (this.ws?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    try {
      this.ws = new WebSocket(buildWebSocketUrl('/ws/mineral-discovery/progress'));
      
      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.attemptReconnect();
    }
  }

  private handleMessage(message: WebSocketMessage) {
    if (!this.dispatch) return;

    switch (message.type) {
      case 'progress':
        this.dispatch(updateAnalysisProgress({
          progress: message.data.progress || 0,
          message: message.data.message || '',
          estimatedTimeRemaining: message.data.estimatedTime
        }));
        break;

      case 'status':
        this.dispatch(setAnalysisStatus({
          status: 'running',
          message: message.data.message || '',
          id: message.data.analysisId || ''
        }));
        break;

      case 'completed':
        this.dispatch(setAnalysisStatus({
          status: 'completed',
          progress: 100,
          message: 'Analysis completed successfully'
        }));
        // Optionally dispatch action to load results
        if (message.data.results) {
          // Handle results data
          }
        break;

      case 'error':
        this.dispatch(setAnalysisStatus({
          status: 'failed',
          message: message.data.error || 'Analysis failed with unknown error'
        }));
        break;

      default:
        }
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff

    setTimeout(() => {
      if (this.dispatch) {
        this.connect(this.dispatch);
      }
    }, delay);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.dispatch = null;
    this.reconnectAttempts = 0;
  }

  send(message: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      }
  }
}

// Global WebSocket manager instance
const wsManager = new WebSocketManager();

// WebSocket middleware
export const websocketMiddleware: Middleware = (storeAPI) => (next) => (action) => {
  const result = next(action);

  // Handle specific actions that should trigger WebSocket operations
  if (action && typeof action === 'object' && 'type' in action) {
    switch (action.type) {
      case 'mineralDiscovery/runAnalysis/pending':
        // Connect WebSocket when analysis starts
        wsManager.connect(storeAPI.dispatch);
        break;

      case 'mineralDiscovery/stopAnalysis/fulfilled':
      case 'mineralDiscovery/runAnalysis/rejected':
        // Optionally disconnect when analysis stops or fails
        // wsManager.disconnect();
        break;

      default:
        break;
    }
  }

  return result;
};

export default wsManager;
